/**
 * Create a wpm namespace under which all functions are declared
 */

// https://stackoverflow.com/a/5947280/4688612

(function (pmw, $, undefined) {

	const pmwDeduper = {
		keyName          : "_wpm_order_ids",
		cookieExpiresDays: 365,
	};

	const REST_AVAILABLE_KEY = "restEndpointAvailable";

	pmw.emailSelected         = false;
	pmw.paymentMethodSelected = false;

	/**
	 * Returns the cached REST availability status.
	 * - true:      REST was tested and works
	 * - false:     REST was tested and failed
	 * - undefined: not tested yet
	 */
	pmw.getRestStatus = () => pmw.retrieveData(REST_AVAILABLE_KEY);

	/**
	 * Try a request via REST first; if REST fails (or is already known to be
	 * unavailable), fall back to AJAX so the request is never lost.
	 *
	 * On the very first call in a session the REST status is unknown, so we
	 * optimistically attempt REST. The result is cached for the session:
	 *   - success → future calls go straight to REST (incl. sendBeacon)
	 *   - failure → future calls skip REST and use AJAX directly
	 *
	 * @param {Object}          restOptions            fetch() init for the REST call
	 * @param {string}          restOptions.url        Full REST URL
	 * @param {RequestInit}     restOptions.init       fetch() options (method, headers, body…)
	 * @param {Function|null}   ajaxFallback           Callback that performs the AJAX request.
	 *                                                 Receives no arguments; the caller closes
	 *                                                 over the payload. Return value is ignored.
	 *                                                 Pass null to skip AJAX fallback entirely.
	 * @returns {Promise<Response|null>}               The fetch Response on REST success, or null
	 *                                                 when AJAX fallback was used (or both failed).
	 */
	pmw.tryRestOrAjax = async (restOptions, ajaxFallback = null) => {

		// REST already known to be unavailable — go straight to AJAX
		if (pmw.getRestStatus() === false) {
			if (ajaxFallback) ajaxFallback();
			return null;
		}

		try {
			const response = await fetch(restOptions.url, restOptions.init);

			if (response.ok) {
				if (pmw.getRestStatus() !== true) {
					pmw.storeData(REST_AVAILABLE_KEY, true);
				}
				return response;
			}

			// Non-OK status — mark REST unavailable
			pmw.storeData(REST_AVAILABLE_KEY, false);
			pmw.console.error("REST request failed (HTTP " + response.status + "), falling back to AJAX");

		} catch (e) {
			pmw.storeData(REST_AVAILABLE_KEY, false);
			pmw.console.error("REST request error, falling back to AJAX: ", e);
		}

		// REST failed — replay via AJAX so the request is not lost
		if (ajaxFallback) ajaxFallback();
		return null;
	};

	/**
	 * This function writes the provided orderId into storage. It will first retrieve the existing orderIds
	 * from the storage if any, then add the new orderId into this existing array of orderIds  and stores
	 * the updated array back to the storage.
	 * If the function pmw.storeOrderIdOnServer exists and order_duplication_prevention is set to true in pmwDataLayer.shop,
	 * this function will also store the orderId, orderKey and source on the server side using pmw.storeOrderIdOnServer.
	 *
	 * @param {String}  orderId  - The ID of the order to be stored.
	 * @param {String}  orderKey - The Key of the order to be stored.
	 * @param {String}  source - The source from which the order originated (default: 'thankyou_page').
	 */
	pmw.writeOrderIdToStorage = (orderId, orderKey, source = "thankyou_page") => {

		// Get existing order ids
		let orderIds = pmw.retrieveData("orderIds", true) || [];

		// Add the new order id
		orderIds.push(orderId);

		// Store the order ids
		pmw.storeData("orderIds", orderIds, true);

		if (typeof pmw.storeOrderIdOnServer === "function") {
			pmw.storeOrderIdOnServer({
				orderId : orderId,
				orderKey: orderKey,
				source  : source,
			});
		}
	};

	pmw.isOrderIdStored = orderId => {

		// If order deduplication is disabled, return false
		if (!pmwDataLayer.shop.order_duplication_prevention) {
			pmw.console.log("order duplication prevention is off");
			return false;
		}

		const orderIds = pmw.retrieveData("orderIds", true) || [];
		return orderIds.includes(orderId);
	};

	/**
	 * Check if the email address is valid
	 *
	 * https://stackoverflow.com/questions/201323/how-can-i-validate-an-email-address-using-a-regular-expression
	 * https://www.w3.org/TR/html5/forms.html#valid-e-mail-address
	 * https://fightingforalostcause.net/content/misc/2006/compare-email-regex.php
	 *
	 * @param email
	 * @returns {boolean}
	 */
	pmw.isEmail = email => {

		/**
		 * GitHub Copilot generated RFC 5322 compliant regex
		 * - Don't allow emails without a top-level domain like "john@localhost"
		 * - Don't allow emails with dots at the end of the name like "john.doe.@example.com"
		 */
		const regex = new RegExp(
			"^(([^<>()\\[\\]\\\\.,;:\\s@\"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@\"]+)*)|(\".+\"))"
			+ "@"
			+ "((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])"
			+ "|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$",
		);

		return regex.test(email);
	};

	pmw.removeProductFromCart = async (productId, quantityToRemove = null) => {

		try {

			if (!productId) throw Error("Wasn't able to retrieve a productId");

			productId = pmw.getIdBasedOndVariationsOutputSetting(productId);

			if (!productId) throw Error("Wasn't able to retrieve a productId");

			let quantity;

			if (quantityToRemove == null) {
				quantity = pmwDataLayer.cart[productId].quantity;
			} else {
				quantity = quantityToRemove;
			}

			if (!pmwDataLayer.cart[productId]) {
				await pmw.getProductsFromBackend([productId]);
			}

			let product = pmw.getProductDetailsFormattedForEvent(productId, quantity);

			jQuery(document).trigger("pmw:remove-from-cart", product);

			if (quantityToRemove == null || pmwDataLayer.cart[productId].quantity === quantityToRemove) {
				delete pmwDataLayer.cart[productId];
			} else {
				pmwDataLayer.cart[productId].quantity = pmwDataLayer.cart[productId].quantity - quantity;
			}

			pmw.storeCartData(pmwDataLayer.cart);

		} catch (e) {
			pmw.console.error("error: ", e);
		}
	};

	pmw.getIdBasedOndVariationsOutputSetting = productId => {

		try {
			if (pmwDataLayer?.shop?.variations_output) return productId;

			if (pmwDataLayer?.products?.[productId]?.is_variation) return pmwDataLayer.products[productId].parent_id;

			return productId;

		} catch (e) {
			console.error(e);
		}
	};

	pmw.prepareSelectors = (selectors, type) => {

		if (pmwDataLayer?.shop?.selectors?.[type]) {

			// merge the selectors from the addToCartSelectors array with the selectors from the pmwDataLayer.shop.selectors.addToCart array
			selectors = selectors.concat(pmwDataLayer.shop.selectors[type]);

			// Remove duplicates
			selectors = [...new Set(selectors)];

			// If one of the selectors is document, remove it
			if (selectors.indexOf("document") >= 0) {
				selectors.splice(selectors.indexOf("document"), 1);
			}

			// If one of the selectors is body, remove all others
			if (selectors.indexOf("body") >= 0) {
				selectors = ["body"];
			}
		}

		return selectors.join(", ");
	};

	// add_to_cart
	pmw.addProductToCart = async (productId, quantity) => {

		// Cast quantity to number to make sure it's a number
		quantity = Number(quantity);

		try {

			if (!productId) throw Error("Wasn't able to retrieve a productId");

			productId = pmw.getIdBasedOndVariationsOutputSetting(productId);

			if (!productId) throw Error("Wasn't able to retrieve a productId");

			if (!pmwDataLayer?.products?.[productId]) {
				await pmw.getProductsFromBackend([productId]);
			}

			let product = pmw.getProductDetailsFormattedForEvent(productId, quantity);

			jQuery(document).trigger("pmw:add-to-cart", product);

			// add product to cart pmwDataLayer['cart']

			// if the product already exists in the object, only add the additional quantity
			// otherwise create that product object in the pmwDataLayer['cart']
			if (pmwDataLayer?.cart[productId]) {

				pmwDataLayer.cart[productId].quantity = pmwDataLayer.cart[productId].quantity + quantity;
			} else {

				if (!("cart" in pmwDataLayer)) pmwDataLayer.cart = {};

				pmwDataLayer.cart[productId] = pmw.getProductDetailsFormattedForEvent(productId, quantity);
			}

			pmw.storeCartData(pmwDataLayer.cart);

		} catch (e) {
			console.error(e);

			// fallback if pmwDataLayer.cart and pmwDataLayer.products got out of sync in case cart caching has an issue
			pmw.getCartItemsFromBackend();
		}
	};

	pmw.initCart = () => {

		const storedCart = pmw.retrieveCartData();

		// If the WC cart doesn't exist, eg. after a confirmed purchase,
		// and we still have a cart object in the storage, reset it.
		if (!pmw.doesWooCommerceCartExist()) {

			if (Object.keys(storedCart).length !== 0) {
				pmw.storeCartData({});
			}

			pmw.deleteDataKey("cart_hash");

			// The cart is gone, so re-arm the checkout-page-load begin_checkout
			// trigger. The next checkout in this session is a new one.
			pmw.deleteDataKey("begin_checkout_fired");

			return;
		}

		/**
		 * The WC cart exists server-side. Detect carts that were populated or
		 * changed outside of client-side tracking (funnel builders such as
		 * CartFlows adding products while rendering a page, persistent carts
		 * restored on login, coupon auto-adds): either the stored cart is
		 * empty even though the cart cookie says the cart has items, or the
		 * WooCommerce cart hash cookie no longer matches the hash recorded at
		 * the last sync. In both cases the server cart is the source of
		 * truth, so re-sync it from the backend.
		 *
		 * The stored hash is kept current for client-tracked changes through
		 * the added_to_cart / removed_from_cart events (see
		 * event_listeners.js), so this fetch only runs on genuine desyncs.
		 */
		const serverCartHash = pmw.getCookie("woocommerce_cart_hash");
		const storedCartHash = pmw.retrieveData("cart_hash");

		if (
			Object.keys(storedCart).length === 0
			|| (serverCartHash && serverCartHash !== storedCartHash)
		) {
			pmw.storeCartData({});
			pmw.getCartItemsFromBackend();
			return;
		}

		pmw.saveCartObjectToDataLayer(storedCart);
	};

	/**
	 * Whether a begin_checkout event has already fired for the current cart.
	 *
	 * Every begin_checkout stamps this flag (see the pmw:begin-checkout listener
	 * in event_listeners.js), and pmw.initCart resets it when the WooCommerce
	 * cart ceases to exist, so the flag lives exactly as long as the cart it
	 * belongs to (within the session).
	 */
	pmw.hasBeginCheckoutFired = () => true === pmw.retrieveData("begin_checkout_fired");

	/**
	 * Trigger the begin_checkout event once the cart has been synced.
	 *
	 * pmw.initCart may fetch the cart from the backend asynchronously (e.g. on
	 * funnel pages where the cart was populated server-side), so wait for
	 * pmwDataLayer.cart to be populated before firing, to make sure the event
	 * carries the products, value, and currency. After ~5 seconds, fire anyway:
	 * the WooCommerce cart cookie says the cart has items, and an event without
	 * commerce fields is better than a missing funnel step.
	 */
	pmw.triggerBeginCheckoutWhenCartReady = (attempt = 0) => {

		// A begin_checkout may have fired while waiting, e.g. through a
		// custom snippet. Never duplicate it.
		if (pmw.hasBeginCheckoutFired()) return;

		if (pmwDataLayer?.cart && Object.keys(pmwDataLayer.cart).length > 0) {
			jQuery(document).trigger("pmw:begin-checkout");
			return;
		}

		// The backend answered and the cart is genuinely empty (the items
		// cookie was stale): nothing is being checked out, don't fire.
		if (pmw.cartSyncedFromBackend) return;

		if (attempt >= 20) {
			jQuery(document).trigger("pmw:begin-checkout");
			return;
		}

		setTimeout(() => pmw.triggerBeginCheckoutWhenCartReady(attempt + 1), 250);
	};

	// get all cart items from the backend
	pmw.getCartItemsFromBackend = () => {
		try {

			/**
			 * Can't use a REST API endpoint, as the cart session will not be loaded if the
			 * endpoint is called.
			 *
			 * https://wordpress.org/support/topic/wc-cart-is-null-in-custom-rest-api/#post-11442843
			 */

			/**
			 * Get the cart items from the backend the data object using fetch API
			 * and log success or error messages
			 * and url encoded data
			 */
			fetch(pmw.ajax_url, {
				method   : "POST",
				cache    : "no-cache",
				body     : new URLSearchParams({action: "pmw_get_cart_items"}),
				keepalive: true,
				redirect : "error",
			})
				.then(response => {
					if (response.ok) {
						return response.json();
					} else {
						throw Error("Error getting cart items from backend");
					}
				})
				.then(data => {

					if (data.success) {

						if (!data.data["cart"]) data.data["cart"] = {};
						pmw.saveCartObjectToDataLayer(data.data["cart"]);
						pmw.storeCartData(data.data["cart"]);

						// The data layer cart now reflects the server cart. Lets
						// waiters (pmw.triggerBeginCheckoutWhenCartReady) tell a
						// genuinely empty cart apart from a pending sync.
						pmw.cartSyncedFromBackend = true;

						// Record the WooCommerce cart hash this sync corresponds to,
						// so initCart only re-fetches when the cart changes outside
						// of client-side tracking.
						pmw.storeData("cart_hash", pmw.getCookie("woocommerce_cart_hash"));

						// Save cart_item_keys from AJAX response for remove_from_cart tracking
						// This provides fallback when inline scripts are suppressed via pmw_output_cart_item_data filter
						if (data.data["cart_item_keys"]) {
							pmwDataLayer.cart_item_keys = Object.assign(
								pmwDataLayer.cart_item_keys || {},
								data.data["cart_item_keys"]
							);
						}

					} else {
						throw Error("Error getting cart items from backend");
					}
				})
				.catch(error => {
					// The surrounding try/catch cannot catch async rejections. Without
					// this handler a blocked endpoint would fail silently. The cart
					// hash is deliberately not stored on failure, so the next page
					// load retries the sync once the endpoint is reachable again.
					pmw.console.error("Pixel Manager: getting cart items from backend failed", error);
				});

		} catch (e) {
			console.error(e);
		}
	};

	// Get product IDs from the backend
	pmw.getProductsFromBackend = async productIds => {

		if (pmwDataLayer?.products) {
			// If productIds already exists as key in pmwDataLayer.products, remove it from the array
			productIds = productIds.filter(productId => !(productId in pmwDataLayer.products));
		}

		// If no product IDs are in the object, don't try to get anything from the server
		if (!productIds || productIds.length === 0) return;

		// if no page_id is set, don't try to get anything from the server
		if (!pmwDataLayer.general.page_id) return;

		// if productIds is not an array, or it's an empty array, don't try to get anything from the server
		if (!Array.isArray(productIds) || productIds.length === 0) return;

		try {

			let data = {
				action     : "pmw_get_product_ids",  // for the AJAX call
				page_id    : pmwDataLayer.general.page_id,
				page_type  : pmwDataLayer.shop.page_type,
				product_ids: productIds,
			};

			const restResponse = await pmw.tryRestOrAjax(
				{
					url : pmw.root + "pmw/v1/products/",
					init: {
						method : "POST",
						cache  : "no-cache",
						headers: {"Content-Type": "application/json"},
						body   : JSON.stringify(data),
					},
				},
				() => {
					// AJAX fallback — fire-and-forget; product data will be missing but
					// tracking still works on pages that already have it in the data layer.
					fetch(pmw.ajax_url, {
						method: "POST",
						cache : "no-cache",
						body  : new URLSearchParams(data),
					})
						.then(r => r.json())
						.then(responseData => {
							if (responseData.success) {
								pmwDataLayer.products = Object.assign({}, pmwDataLayer.products, responseData.data);
							}
						})
						.catch(e => console.error(e));
				},
			);

			if (restResponse) {
				let responseData = await restResponse.json();

				if (!responseData.success) {
					throw new Error("Error getting products from backend: " + responseData.data);
				}

				pmwDataLayer.products = Object.assign({}, pmwDataLayer.products, responseData.data);
			}

		} catch (e) {
			console.error(e);
		}

		return true;
	};

	pmw.saveCartObjectToDataLayer = cartObject => {

		pmwDataLayer.cart     = cartObject;
		pmwDataLayer.products = Object.assign({}, pmwDataLayer.products, cartObject);
	};

	pmw.triggerViewItemEventPrep = async productId => {

		if (!pmwDataLayer?.products?.[productId]) {
			await pmw.getProductsFromBackend([productId]);
		}

		if (!pmwDataLayer?.products?.[productId]) {
			pmw.triggerViewItemEventNoProduct();
			pmw.console.log(`Product with ID ${productId} not found in the data layer and couldn't be retrieved from the backend. Triggering view-item event without product data.`);
		} else {
			pmw.triggerViewItemEvent(productId);
		}
	};

	pmw.triggerViewItemEvent = productId => {

		let product = pmw.getProductDetailsFormattedForEvent(productId);

		if (!product) return;

		jQuery(document).trigger("pmw:view-item", product);
	};

	pmw.triggerViewItemEventNoProduct = () => {
		jQuery(document).trigger("pmw:view-item");
	};

	pmw.fireCheckoutOption = (step, checkout_option = null, value = null) => {

		let data = {
			step           : step,
			checkout_option: checkout_option,
			value          : value,
		};

		jQuery(document).trigger("pmw:checkout-option", data);
	};

	pmw.fireCheckoutProgress = step => {

		let data = {
			step: step,
		};

		jQuery(document).trigger("pmw:checkout-progress", data);
	};

	pmw.getPostIdFromString = string => {

		try {
			return string.match(/(post-)(\d+)/)[2];
		} catch (e) {
			console.error(e);
		}
	};

	pmw.triggerViewItemList = productId => {

		if (!productId) throw Error("Wasn't able to retrieve a productId");

		productId = pmw.getIdBasedOndVariationsOutputSetting(productId);

		if (!productId) throw Error("Wasn't able to retrieve a productId");

		jQuery(document).trigger("pmw:view-item-list", pmw.getProductDataForViewItemEvent(productId));
	};

	pmw.getProductDataForViewItemEvent = productId => {

		if (!productId) throw Error("Wasn't able to retrieve a productId");

		try {
			if (pmwDataLayer.products[productId]) {

				return pmw.getProductDetailsFormattedForEvent(productId);
			}
		} catch (e) {
			console.error(e);
		}
	};

	pmw.getMainProductIdFromProductPage = () => {

		try {
			if (["simple", "variable", "grouped", "composite", "bundle"].indexOf(pmwDataLayer.shop.product_type) >= 0) {
				return jQuery(".pmwProductId:first").data("id");
			} else {
				return false;
			}
		} catch (e) {
			console.error(e);
		}
	};

	pmw.viewItemListTriggerTestMode = target => {

		jQuery(target).css({"position": "relative"});
		jQuery(target).append("<div id=\"viewItemListTriggerOverlay\"></div>");
		jQuery(target).find("#viewItemListTriggerOverlay").css({
			"z-index"         : "10",
			"display"         : "block",
			"position"        : "absolute",
			"height"          : "100%",
			"top"             : "0",
			"left"            : "0",
			"right"           : "0",
			"opacity"         : pmwDataLayer.shop.view_item_list_trigger.opacity,
			"background-color": pmwDataLayer.shop.view_item_list_trigger.background_color,
		});
	};

	pmw.getSearchTermFromUrl = () => {

		try {
			let urlParameters = new URLSearchParams(window.location.search);
			return urlParameters.get("s");
		} catch (e) {
			console.error(e);
		}
	};

	// we need this to track timeouts for intersection observers
	let ioTimeouts = {};

	pmw.observerCallback = (entries, observer) => {

		entries.forEach((entry) => {

			try {
				let productId;

				let elementId = jQuery(entry.target).data("ioid");

				// Get the productId from next element, if wpmProductId is a sibling, like in Gutenberg blocks
				// otherwise go search in children, like in regular WC loop items
				if (jQuery(entry.target).next(".pmwProductId").length) {
					// console.log('test 1');
					productId = jQuery(entry.target).next(".pmwProductId").data("id");
				} else {
					productId = jQuery(entry.target).find(".pmwProductId").data("id");
				}


				if (!productId) throw Error("pmwProductId element not found");

				if (entry.isIntersecting) {

					ioTimeouts[elementId] = setTimeout(() => {

						pmw.triggerViewItemList(productId);
						if (pmwDataLayer.shop.view_item_list_trigger.test_mode) pmw.viewItemListTriggerTestMode(entry.target);
						if (pmwDataLayer.shop.view_item_list_trigger.repeat === false) observer.unobserve(entry.target);
					}, pmwDataLayer.shop.view_item_list_trigger.timeout);

				} else {

					clearTimeout(ioTimeouts[elementId]);
					if (pmwDataLayer.shop.view_item_list_trigger.test_mode) jQuery(entry.target).find("#viewItemListTriggerOverlay").remove();
				}
			} catch (e) {
				console.error(e);
			}
		});
	};

	// fire view_item_list only on products that have become visible
	let io;
	let ioid = 0;

	const getCommonIdentifiersToWatch = () => {

		let listOfClassNames = {
			nested: [],
			flat  : [],
		};

		try {

			const haveSameNestingDepth = (element1, element2) => {

				// If both are the same, return the parent
				return jQuery(element1).parents().length === jQuery(element2).parents().length;
			};

			const findCommonParentElement = (element1, element2) => {
				// If both are the same, return the parent
				if (jQuery(element1).parent().is(jQuery(element2).parent())) {
					return {
						node: jQuery(element1).parent(),
						type: "flat",
					};
				}

				return {
					node: jQuery(element1).parents().has(jQuery(element2).parents()).first(),
					type: "nested",
				};
			};

			const countOfChildren = (element) => {
				// Return the count of "wpmProductId" elements
				return jQuery(element).find(".pmwProductId").length;
			};

			const getCommonClass = (classes1, classes2) => {

				// console.log("classes1", classes1);
				// console.log("classes2", classes2);
				// classes 1 and 2 are arrays of classes
				// They may contain the same classes, but not in the same order
				// Return the first common class that's present in both arrays

				return classes1.filter(value => classes2.includes(value))[0] || null;
			};

			const pmwProductIds = document.querySelectorAll(".pmwProductId");

			// If zero or only one product template is found, then we can't run the algorithm
			if (pmwProductIds.length === 1) {
				// console.log("No product or too few templates found")
				return listOfClassNames;
			}

			// console.log("wpmProductIds", wpmProductIds);
			// console.log("looping")

			for (let i = 0; i < pmwProductIds.length - 1; i++) {

				// console.log("i", i)

				if (!haveSameNestingDepth(pmwProductIds[i], pmwProductIds[i + 1])) {
					// console.log("The nesting depth is not the same")
					i++;
					continue;
				}

				let productsNode = findCommonParentElement(pmwProductIds[i], pmwProductIds[i + 1]);
				// console.log("productsNode.node", productsNode.node)
				// console.log("productsNode.type", productsNode.type)

				// If the productsNode is nested, get the first class of the first child. Then console log the class
				if (productsNode.type === "nested") {

					let firstChildClassAttr = productsNode.node.children().first().attr("class");
					let firstChildClasses   = firstChildClassAttr ? firstChildClassAttr.split(" ") : [];

					let secondChildClassAttr = productsNode.node.children().first().next().attr("class");
					let secondChildClasses   = secondChildClassAttr ? secondChildClassAttr.split(" ") : [];

					const commonClass = getCommonClass(firstChildClasses, secondChildClasses);

					listOfClassNames.nested.push(commonClass);
				} else if (productsNode.type === "flat") {

					// If class is not found, continue
					if (!productsNode.node.children().first().attr("class")) continue;

					// firstProductTemplateClasses is the classes of the first child element of the productsNode
					let firstProductTemplateClasses = productsNode.node.children().first().attr("class").split(" ")[0];

					listOfClassNames.flat.push(firstProductTemplateClasses);
				}

				// which wpmProductIds is the last one in the productsNode? console log the index of the last wpmProductId in the productsNode
				// console.log("productsNode.children().length", countOfChildren(productsNode));

				i = i + countOfChildren(productsNode.node) - 1;
			}

			// Make the list of class names unique
			listOfClassNames.nested = [...new Set(listOfClassNames.nested)];
			listOfClassNames.flat   = [...new Set(listOfClassNames.flat)];

			return listOfClassNames;

		} catch (e) {
			console.error(e);
			return listOfClassNames;
		}
	};


	let detectedProductTemplateClasses = {
		nested: [],
		flat  : [],
	};

	const isNestedClass = (elem) => {
		let nestedClassNames = [
			"product",
			"type-product",
			"product-item-inner",
			...detectedProductTemplateClasses.nested,
		];

		return nestedClassNames.some(className => jQuery(elem).closest("." + className).length);
	};

	const isFlatClass = (elem) => {

		let flatClassNames = [
			"wc-block-grid__product",
			"product",
			"product-small",
			"woocommerce-LoopProduct-link",
			...detectedProductTemplateClasses.flat,
		];

		return flatClassNames.some(className => jQuery(elem).prev().hasClass(className));
	};

	const getAllElementsToWatch = () => {

		detectedProductTemplateClasses = getCommonIdentifiersToWatch();

		return jQuery(".pmwProductId")
			.map(function (i, elem) {

				if (isNestedClass(elem)) {
					return jQuery(elem).parent();
				} else if (isFlatClass(elem)) {
					return jQuery(this).prev();
				} else if (jQuery(elem).closest(".product").length) {
					return jQuery(elem).closest(".product");
				}
			});
	};

	pmw.startIntersectionObserverToWatch = () => {

		try {
			// enable view_item_list test mode from browser
			if (pmw.urlHasParameter("vildemomode")) pmwDataLayer.shop.view_item_list_trigger.test_mode = true;

			// set up intersection observer
			// https://stackoverflow.com/questions/62084306/intersectionobserver-not-working-in-safari-or-ios
			io = new IntersectionObserver(pmw.observerCallback, {
				root: null,
				// root     : document.body,
				threshold: pmwDataLayer.shop.view_item_list_trigger.threshold,
			});

			getAllElementsToWatch().each((i, elem) => {

				jQuery(elem[0]).data("ioid", ioid++);

				io.observe(elem[0]);
			});
		} catch (e) {
			console.error(e);
		}
	};

	// Watch DOM for new lazy loaded products and add them to the intersection observer
	pmw.startProductsMutationObserverToWatch = () => {

		try {
			// Pass in the target node, as well as the observer options

			// selects the most common parent node
			// https://stackoverflow.com/a/7648323/4688612
			let productsNode = jQuery(".pmwProductId:eq(0)").parents().has(jQuery(".pmwProductId:eq(1)").parents()).first();

			if (productsNode.length) {
				productsMutationObserver.observe(productsNode[0], {
					attributes   : true,
					childList    : true,
					characterData: true,
				});
			}
		} catch (e) {
			console.error(e);
		}
	};

	// Create an observer instance
	const productsMutationObserver = new MutationObserver(mutations => {

		mutations.forEach(mutation => {
			let newNodes = mutation.addedNodes; // DOM NodeList
			if (newNodes !== null) { // If there are new nodes added
				let nodes = jQuery(newNodes); // jQuery set
				nodes.each((i, node) => {
					if (
						// jQuery(this).hasClass("type-product") ||
						// jQuery(this).hasClass("product-small") ||
						// jQuery(this).hasClass("wc-block-grid__product")
						// jQuery(this).hasClass("type-product")
						isNestedClass(node) || isFlatClass(node)
					) {
						// check if the node has a child or sibling wpmProductId
						// if yes add it to the intersectionObserver
						if (hasPmwProductIdElement(node)) {
							jQuery(node).data("ioid", ioid++);
							io.observe(node);
						}
					}
				});
			}
		});
	});

	let hasPmwProductIdElement = elem =>
		!!(jQuery(elem).find(".pmwProductId").length ||
			jQuery(elem).siblings(".pmwProductId").length);

	/**
	 * Sets a new cookie or updates an existing one with the provided name and value.
	 *
	 * @function setCookie
	 * @global
	 * @param {string} cookieName - The name of the cookie.
	 * @param {string} [cookieValue=""] - The value to set for the cookie. Default value is an empty string.
	 * @param {number|null} [expiryDays=null] - The number of days until the cookie expires. If not provided, the cookie will last until the end of the session.
	 * @returns {void}
	 *
	 * @example
	 * // This will create a cookie "username" with the value "JohnDoe" that expires in 7 days
	 * pmw.setCookie("username", "JohnDoe", 7);
	 */
	pmw.setCookie = (cookieName, cookieValue = "", expiryDays = null) => {

		if (expiryDays) {

			let d = new Date();
			d.setTime(d.getTime() + (expiryDays * 24 * 60 * 60 * 1000));
			let expires     = "expires=" + d.toUTCString();
			document.cookie = cookieName + "=" + cookieValue + ";" + expires + ";path=/";
		} else {
			document.cookie = cookieName + "=" + cookieValue + ";path=/";
		}
	};

	/**
	 * Retrieves the value of a specific cookie from the document's cookie string.
	 *
	 * @param {String} cookieName - The name of the cookie to retrieve.
	 * @returns {String} - The value of the cookie if found, an empty string otherwise.
	 *
	 * @example
	 * // returns 'value' if 'cookieName=value' exists in document.cookie
	 * pmw.getCookie('cookieName')
	 */
	/**
	 * Recursively strip null and undefined values from an event payload.
	 *
	 * Some pixel endpoints reject an entire request when a property is
	 * explicitly null (TikTok Events API), and optional chaining fallbacks
	 * can leave undefined values in adapter output (e.g. currency when the
	 * shop object is unavailable). The event processor applies this to every
	 * adapter result so no pixel receives null or undefined parameters.
	 *
	 * @since 1.61.1
	 *
	 * @param {*} value Value to sanitize.
	 * @returns {*} The value with null/undefined entries removed.
	 */
	pmw.stripNullish = value => {

		if (Array.isArray(value)) {
			return value
				.map(item => pmw.stripNullish(item))
				.filter(item => item !== null && item !== undefined);
		}

		if (value && typeof value === "object") {
			const out = {};
			for (const [ key, val ] of Object.entries(value)) {
				if (val === null || val === undefined) continue;
				out[key] = pmw.stripNullish(val);
			}
			return out;
		}

		return value;
	};

	pmw.getCookie = cookieName => {

		let name          = cookieName + "=";
		let decodedCookie = decodeURIComponent(document.cookie);
		let ca            = decodedCookie.split(";");

		for (let i = 0; i < ca.length; i++) {

			let c = ca[i];

			while (c.charAt(0) == " ") {
				c = c.substring(1);
			}

			if (c.indexOf(name) == 0) {
				return c.substring(name.length, c.length);
			}
		}

		return "";
	};

	/**
	 * Find and get the cookie with a name that contains the string.
	 * If one is found, then return the cookie.
	 * If none is found, then return false.
	 *
	 * @param string
	 * @returns {boolean|string}
	 *
	 * @since 1.32.5
	 */
	pmw.getCookieThatContainsRegex = (regex) => {

		let cookies = document.cookie.split(";");

		for (let i = 0; i < cookies.length; i++) {
			let cookie = cookies[i].trim();

			if (regex.test(cookie)) {
				return cookie;
			}
		}

		return false;
	};

	pmw.deleteCookie = cookieName => {
		pmw.setCookie(cookieName, "", -1);
	};

	/**
	 * Store data in the browser
	 *
	 * Try to store the data in the local storage (session or persistent) if available.
	 * If not available, store the data in the cookie.
	 *
	 * Max cookie expiry is 400 days as specified by Google Chrome: https://developer.chrome.com/blog/cookie-max-age-expires/
	 *
	 * @param key
	 * @param value
	 * @param persistent
	 * @param extension - If true, this will be saved in a separate key
	 */
	pmw.storeData = (key, value, persistent = false, extension = false) => {

		const suffix = extension ? key : "";

		let dataGetter = persistent ? pmw.getPersistentData : pmw.getSessionData;
		let dataSetter = persistent ? pmw.setPersistentData : pmw.setSessionData;

		let data = dataGetter(suffix);
		extension ? data = value : data[key] = value;

		dataSetter(data, suffix);

		// Broadcast session data changes to other tabs
		if (!persistent && pmw.crossTabSync && !pmw.crossTabSync.isSyncing()) {
			pmw.crossTabSync.broadcastUpdate(key, value, extension, suffix);
		}
	};

	pmw.retrieveData = (key, persistent = false, extension = false) => {

		const suffix = extension ? key : "";

		let data = persistent ? pmw.getPersistentData(suffix) : pmw.getSessionData(suffix);

		return extension ? data : data[key];
	};

	pmw.deleteDataKey = (key, persistent = false, extension = false) => {

		const suffix = extension ? key : "";

		let dataGetter = persistent ? pmw.getPersistentData : pmw.getSessionData;
		let dataSetter = persistent ? pmw.setPersistentData : pmw.setSessionData;

		let data = dataGetter(suffix);
		// extension ? delete data : delete data[key]

		if (extension) {

			try {
				if (persistent && window.localStorage) window.localStorage.removeItem(pmw.getPersistentDataKey(suffix));
				if (!persistent && window.sessionStorage) window.sessionStorage.removeItem(pmw.getSessionDataKey(suffix));

			} catch (e) {
				console.error(e);
			}

			return;
		}

		delete data[key];

		dataSetter(data, suffix);
	};

	pmw.storeCartData = data => {
		pmw.storeData("cart", data, false, true);
	};

	pmw.retrieveCartData = () => {
		return pmw.retrieveData("cart", false, true);
	};

	pmw.getSessionDataKey = (suffix = "") => {
		const defaultStorageKey = "_pmw_session_data";

		if (suffix) {
			return defaultStorageKey + "_" + suffix;
		}

		return defaultStorageKey;
	};

	pmw.getPersistentDataKey = (storageKeySuffix) => {

		const defaultStorageKey = "_pmw_persistent_data";

		if (storageKeySuffix) {
			return defaultStorageKey + "_" + storageKeySuffix;
		}

		return defaultStorageKey;
	};

	pmw.getSessionData = (suffix = "") => {

		if (window.sessionStorage) {

			let data = window.sessionStorage.getItem(pmw.getSessionDataKey(suffix));

			return data !== null ? JSON.parse(data) : {};
		} else {
			// fallback to cookie
			let data = pmw.getCookie(pmw.getSessionDataKey(suffix));

			return data !== "" ? JSON.parse(data) : {};
		}
	};

	pmw.setSessionData = (data, storageKeySuffix = "") => {
		if (window.sessionStorage) {
			window.sessionStorage.setItem(pmw.getSessionDataKey(storageKeySuffix), JSON.stringify(data));
		} else {
			// fallback to cookie
			pmw.setCookie(pmw.getSessionDataKey(storageKeySuffix), JSON.stringify(data));
		}
	};

	pmw.getPersistentData = (storageKeySuffix) => {

		if (window.localStorage) {

			let data = window.localStorage.getItem(pmw.getPersistentDataKey(storageKeySuffix));

			return data !== null ? JSON.parse(data) : {};
		} else {
			// fallback to cookie
			let data = pmw.getCookie(pmw.getPersistentDataKey(storageKeySuffix));

			return data !== "" ? JSON.parse(data) : {};
		}
	};

	pmw.setPersistentData = (data, storageKeySuffix = "") => {

		if (window.localStorage) {
			window.localStorage.setItem(pmw.getPersistentDataKey(storageKeySuffix), JSON.stringify(data));
		} else {
			// fallback to cookie
			pmw.setCookie(pmw.getPersistentDataKey(storageKeySuffix), JSON.stringify(data), 400);
		}
	};

	pmw.storeOrderIdOnServer = async (orderDetails) => {

		try {

			const body = {
				action   : "pmw_purchase_pixels_fired",  // for the AJAX call
				order_id : orderDetails.orderId,
				order_key: orderDetails.orderKey,
				source   : orderDetails.source,
			};

			const restResponse = await pmw.tryRestOrAjax(
				{
					url : pmw.root + "pmw/v1/pixels-fired/",
					init: {
						method   : "POST",
						headers  : {"Content-Type": "application/json"},
						body     : JSON.stringify(body),
						keepalive: true,
						cache    : "no-cache",
						redirect : "error",
					},
				},
				() => {
					pmw.console.log("REST API not available, falling back to AJAX");
					fetch(pmw.ajax_url, {
						method   : "POST",
						body     : new URLSearchParams(body),
						keepalive: true,
						redirect : "error",
					})
						.then(r => r.json())
						.then(responseJson => {
							responseJson.success
								? pmw.console.log("", responseJson.data)
								: pmw.console.error("", responseJson.data);
						})
						.catch(e => console.error(e));
				},
			);

			if (restResponse) {
				const responseJson = await restResponse.json();

				if (responseJson.success) {
					pmw.console.log("", responseJson.data);
				} else {
					pmw.console.error("", responseJson.data);
				}
			}

		} catch (e) {
			console.error(e);
		}
	};

	pmw.getProductIdByCartItemElement = element => {

		const href = jQuery(element).find(".product-remove").find("a").attr("href");
		if (href) return pmw.getProductIdByCartItemKeyUrl(new URL(href));

		const productId = jQuery(element).find("[data-product_id]").first().attr("data-product_id");
		return productId ? productId : null;
	};

	pmw.getProductQuantityByCartItemElement = element => {

		return jQuery(element).find(".qty").val() || null;
	};

	pmw.getProductIdByCartItemKeyUrl = url => {

		let searchParams = new URLSearchParams(url.search);
		let cartItemKey  = searchParams.get("remove_item");

		let productId;

		if (pmwDataLayer.cart_item_keys[cartItemKey]["variation_id"] === 0) {
			productId = pmwDataLayer.cart_item_keys[cartItemKey]["product_id"];
		} else {
			productId = pmwDataLayer.cart_item_keys[cartItemKey]["variation_id"];
		}

		return productId;
	};

	pmw.getAddToCartLinkProductIds = () =>
		jQuery("a").map(function () {
			let href = jQuery(this).attr("href");

			if (href && href.includes("?add-to-cart=")) {
				let matches = href.match(/(add-to-cart=)(\d+)/);
				if (matches) return matches[2];
			}
		}).get();

	pmw.getProductDetailsFormattedForEvent = (productId, quantity = 1) => {

		// If pmwDataLayer.products[productId] doesn't exist, return an empty object
		if (!pmwDataLayer?.products?.[productId]) return null;

		let product = {
			id           : productId.toString(),
			dyn_r_ids    : pmwDataLayer.products[productId].dyn_r_ids,
			name         : pmwDataLayer.products[productId].name,
			list_name    : pmwDataLayer.shop.list_name,
			brand        : pmwDataLayer.products[productId].brand,
			category     : pmwDataLayer.products[productId].category,
			variant      : pmwDataLayer.products[productId].variant,
			list_position: pmwDataLayer.products[productId].position,
			quantity     : quantity,
			price        : pmwDataLayer.products[productId].price,
			currency     : pmwDataLayer?.shop?.currency,
			is_variable  : pmwDataLayer.products[productId].is_variable,
			is_variation : pmwDataLayer.products[productId].is_variation,
			parent_id    : pmwDataLayer.products[productId].parent_id,
		};

		if (product.is_variation) product["parent_id_dyn_r_ids"] = pmwDataLayer.products[productId].parent_id_dyn_r_ids;

		return product;
	};

	pmw.getClidFromBrowser = (clidId = "gclid") => {

		let clidCookieId;

		clidCookieId = {
			gclid: "_gcl_aw",
			dclid: "_gcl_dc",
		};

		if (pmw.getCookie(clidCookieId[clidId])) {

			let clidCookie = pmw.getCookie(clidCookieId[clidId]);
			let matches    = clidCookie.match(/(GCL.[\d]*.)(.*)/);
			return matches[2];
		} else {
			return "";
		}
	};

	pmw.getUserAgent = () => navigator.userAgent;

	pmw.getViewPort = () => ({
		width : Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0),
		height: Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0),
	});


	pmw.consoleLogVersion = () => {
		console.log(pmwDataLayer.version);
	};

	/**
	 * Loads a script and caches it using native browser caching.
	 *
	 * Uses script tag injection which:
	 * - Leverages browser's HTTP cache (respects Cache-Control headers)
	 * - Avoids CORS preflight issues (no custom headers sent)
	 * - Auto-executes the script on load
	 * - Prevents duplicate script loading
	 *
	 * This code is .thenable
	 */

	const loadedScripts = new Set();

	// Script URLs whose load event actually fired. Consumed by the first-run
	// "pixels are live" confirmation to distinguish a truly loaded vendor
	// script (gtag.js) from a merely injected snippet.
	pmw.getLoadedScriptUrls = () => Array.from(loadedScripts);

	pmw.loadScriptAndCacheIt = (url, fallbackUrl) => {

		// Return resolved promise if script already loaded
		if (loadedScripts.has(url)) {
			pmw.console.log("Script already loaded: " + url);
			return Promise.resolve();
		}

		const loadScript = (scriptUrl) => {
			return new Promise((resolve, reject) => {

				// Check if script tag already exists in DOM
				if (document.querySelector(`script[src="${scriptUrl}"]`)) {
					loadedScripts.add(scriptUrl);
					pmw.console.log("Script tag already exists: " + scriptUrl);
					resolve();
					return;
				}

				const script       = document.createElement("script");
				script.src         = scriptUrl;
				script.async       = true;
				script.crossOrigin = "anonymous"; // Enables CORS without sending credentials, avoids preflight issues

				script.onload = () => {
					loadedScripts.add(scriptUrl);
					pmw.console.log("Successfully loaded script: " + scriptUrl);
					resolve();
				};

				script.onerror = () => {
					script.remove(); // Clean up failed script tag
					reject(new Error("Script failed to load: " + scriptUrl));
				};

				document.head.appendChild(script);
			});
		};

		return loadScript(url)
			.catch(() => {
				if (fallbackUrl) {
					pmw.console.log("Primary script failed, loading fallback: " + fallbackUrl);
					return loadScript(fallbackUrl);
				}
				throw new Error("Primary script failed to load, and no fallback URL provided.");
			})
			.catch((error) => {
				if (error.message.includes("fallback")) {
					throw error; // Re-throw if it's already the "no fallback" error
				}
				throw new Error("Both primary and fallback scripts failed to load.");
			});
	};

	pmw.getOrderItemPrice = orderItem => (orderItem.total + orderItem.total_tax) / orderItem.quantity;

	pmw.hasLoginEventFired = () => {
		let data = pmw.getSessionData();
		return data?.loginEventFired;
	};

	pmw.setLoginEventFired = () => {
		let data                = pmw.getSessionData();
		data["loginEventFired"] = true;
		pmw.setSessionData(data);
	};

	pmw.hasAccountCreatedEventFired = () => {
		let data = pmw.getSessionData();
		return data?.accountCreatedEventFired;
	};

	pmw.setAccountCreatedEventFired = () => {
		let data                         = pmw.getSessionData();
		data["accountCreatedEventFired"] = true;
		pmw.setSessionData(data);
	};

	pmw.pageLoaded = async () => new Promise(resolve => {
		(function waitForVar() {
			if ("complete" === document.readyState) return resolve();
			setTimeout(waitForVar, 50);
		})();
	});

	pmw.pageReady = () => {
		return new Promise(resolve => {
			(function waitForVar() {
				if ("interactive" === document.readyState || "complete" === document.readyState) return resolve();
				setTimeout(waitForVar, 50);
			})();
		});
	};

	pmw.isMiniCartActive = () => {
		if (window.sessionStorage) {
			Object.keys(window.sessionStorage).forEach(key => {
				if (key.includes("wc_fragments")) {
					return true;
				}
			});
		}

		return false;
	};

	pmw.doesWooCommerceCartExist = () => document.cookie.includes("woocommerce_items_in_cart");

	pmw.urlHasParameter = parameter => {
		let urlParams = new URLSearchParams(window.location.search);
		return urlParams.has(parameter);
	};

	pmw.getUrlParameter = parameter => {
		let urlParams = new URLSearchParams(window.location.search);
		return urlParams.get(parameter);
	};

	// https://stackoverflow.com/a/60606893/4688612
	pmw.hashAsync = (algo, str) => {
		return crypto.subtle.digest(algo, new TextEncoder("utf-8").encode(str)).then(buf => {
			return Array.prototype.map.call(new Uint8Array(buf), x => (("00" + x.toString(16)).slice(-2))).join("");
		});
	};

	pmw.getCartValue = () => {

		let value = 0;

		if (pmwDataLayer?.cart) {

			for (const key in pmwDataLayer.cart) {
				// content_ids.push(pmwDataLayer.products[key].dyn_r_ids[pmwDataLayer.pixels.facebook.dynamic_remarketing.id_type])

				let product = pmwDataLayer.cart[key];

				value += product.quantity * product.price;
			}
		}

		return value;
	};

	/**
	 * Detect if the current URL contains at least one pattern
	 *
	 * @param patterns
	 * @returns {boolean}
	 */
	pmw.doesUrlContainPatterns = patterns => {

		for (const pattern of patterns) {
			if (new RegExp(pattern).test(window.location.href)) {
				return true;
			}
		}

		return false;
	};

	/**
	 * Detect if the current URL contains at least one pattern that is on the tracking exclusion list
	 *
	 * https://www.linkedin.com/pulse/how-remove-google-robot-problem-via-gtm-facebook-pixel-hjelpdahl/
	 * https://www.youtube.com/watch?v=b4I1ePZt8Z0
	 *
	 * @returns {boolean}
	 */
	pmw.excludeDomainFromTracking = () => {

		let exclude_domains = [
			"appspot.com",
			"translate.google.com",
		];

		if (pmwDataLayer?.general?.exclude_domains) {
			exclude_domains = [...exclude_domains, ...pmwDataLayer.general.exclude_domains];
		}

		// Abort if URL contains excluded domains
		if (exclude_domains.some(domain => window.location.href.includes(domain))) {
			console.debug("Pixel Manager: Aborted due to excluded domain");
			return true;
		}

		return false;
	};

	/**
	 * Get a random event ID
	 *
	 * @param length
	 * @return {string}
	 */
	pmw.getRandomEventId = (length = 16) => {
		let id = "";
		while (id.length < length) {
			id += (Math.random() + 1).toString(36).substring(2);
		}
		return "pmw_" + id.substring(0, length);
	};

	pmw.pmwConsoleMessage = () => {
		let message = "Pixel Manager for WooCommerce: ";
		message += pmwDataLayer.version.pro ? "pro" : "free";
		message += " | distro: " + pmwDataLayer.version.distro;
		if (pmwDataLayer.version.distro === "fms" && pmwDataLayer.version.pro) {
			message += " | active license: " + (pmwDataLayer.version.eligible_for_updates ? "yes" : "no");
		}
		message += " | version: " + pmwDataLayer.version.number;

		if (pmwDataLayer.version.show === true) {
			console.log(message);
		} else {
			pmw.console.log(message);
		}
	};

	pmw.canLoadPremiumFeatures = () => {
		return (pmwDataLayer.version.distro === "fms" && pmwDataLayer.version.pro && pmwDataLayer.version.eligible_for_updates) || pmwDataLayer.version.distro === "wcm";
	};

	/**
	 * State if the pmw:dom:ready event has been fired
	 *
	 * @type {boolean}
	 */
	let pmwDomReadyFired = false;


	/**
	 * Trigger the pmw:dom:ready event
	 *
	 * @returns {void}
	 */
	pmw.triggerPmwDomReadyEvent = () => {

		// If the event has already been fired, return
		if (pmwDomReadyFired) return;

		// If the document is already loaded, fire the event
		if (document.readyState === "complete") {
			firePmwDomReadyEvent();
			return;
		}

		// Continue processing if the DOM has not yet been loaded
		// and attach the event listener to the DOMContentLoaded,
		// once through jQuery and once through the native DOM API
		// to maximize compatibility

		// If jQuery is available, use it to fire add the event listener
		jQuery(() => {
			firePmwDomReadyEvent();
		});

		// Also add the event listener using the native DOM API
		document.addEventListener("DOMContentLoaded", () => {
			firePmwDomReadyEvent();
		}, true);
	};

	/**
	 * Fire the pmw:dom:ready event
	 *
	 * @returns {void}
	 */
	const firePmwDomReadyEvent = () => {

		// If the event has already been fired, return
		if (pmwDomReadyFired) return;

		// Fire the event
		document.dispatchEvent(new Event("pmw:dom:ready"));

		// Set the flag to true to indicate that the event has been fired
		pmwDomReadyFired = true;
	};

	pmw.getEmailFromTarget = target => {

		// Only try to add the clicked email if href is set
		if (target.href) {

			// Get the email from the link
			// But only if there is a valid email address in the link
			// Also, the href may contain other parameters, so we need to check for that and remove them
			let email = target.href.replace("mailto:", "");


			if (email.indexOf("?") > -1) {
				email = email.split("?")[0];
			}

			// Trim the email and remove all whitespaces
			email = email.replace(/\s/g, "");

			// If the email is not empty and valid, add it to the data object
			if (email && pmw.isEmail(email)) {
				return email;
			}
		}

		return "";
	};

	pmw.sendEventPayloadToServer = payload => {
		if (typeof pmw.sendEventPayloadToServerPremium === "function") {
			pmw.sendEventPayloadToServerPremium(payload);
		}
	};

	/**
	 * Waits for all active pixels to load and then triggers the page view event.
	 *
	 * @return {Promise<unknown>}
	 *
	 * @since 1.49.0
	 */
	pmw.waitForPixelsAndTriggerPageView = async () => {

		// Guard against duplicate page view events (e.g. when consent API re-triggers pmw:load-pixels)
		if (pmw._pageViewTriggered) {
			pmw.console.log("Page view event already triggered, skipping duplicate.");
			return Promise.resolve();
		}
		pmw._pageViewTriggered = true;

		return new Promise(resolve => {

			// Check if at least one pixel is active in the data layer
			if (!pmwDataLayer?.pixels) {
				pmw.console.error("pmwDataLayer or pmwDataLayer.pixels is not defined, triggering page view event immediately.");
				document.dispatchEvent(new Event("pmw:page-view"));
				resolve();
				return;
			}

			let activePixels = pmwDataLayer?.general?.server_2_server?.pageview_event_s2s?.pixels || [];

			// This line filters the activePixels array, keeping only those pixel names that exist as keys
			// in the pmwDataLayer.pixels object (i.e., only pixels that are currently defined/active in the data layer)
			activePixels = activePixels.filter(pixelName => pmwDataLayer.pixels[pixelName]);

			// If no pixels to check, trigger event immediately
			if (activePixels.length === 0) {
				pmw.console.log("No specific pixels to wait for, triggering page view event.");
				document.dispatchEvent(new Event("pmw:page-view"));
				resolve();
				return;
			}

			// Check if all active pixels are loaded
			const checkPixelsLoaded = () => {
				const allLoaded = activePixels.every(pixelName =>
					!pmwDataLayer.pixels[pixelName] || pmwDataLayer.pixels[pixelName].loaded);

				if (allLoaded) {
					// pmw.console.log("All specified pixels to track s2s page-view events have been loaded. Triggering page-view event.");
					document.dispatchEvent(new Event("pmw:page-view"));
					resolve();
				} else {
					setTimeout(checkPixelsLoaded, 100);
				}
			};

			checkPixelsLoaded();
		});
	};

	pmw.isWooCommerceActive = () => {
		return !!pmwDataLayer.shop;
	};

	/**
	 * Retrieves the product ID associated with a specific pixel.
	 *
	 * @param {string} pixelId - The ID of the pixel to get the product ID for.
	 * @returns {string|null} - The product ID associated with the given pixel ID,
	 *                          or null if no product ID is found.
	 */
	pmw.getProductIdForSpecificPixel = (product, pixelName) => {
		return String(product.dyn_r_ids[pmwDataLayer.pixels[pixelName].dynamic_remarketing.id_type]);
	};

	pmw.isInWooCommerceSesssion = () => {

		if (pmwDataLayer?.shop?.session_active) return true;

		let regex           = new RegExp("woocommerce_cart_hash");
		let wcSessionCookie = pmw.getCookieThatContainsRegex(regex);

		if (wcSessionCookie) return true;

		return false;
	};

	pmw.isNotInWooCommerceSesssion = () => {
		return !pmw.isInWooCommerceSesssion();
	};

	pmw.makeFullUrl = url => {

		// Trim any leading or trailing whitespace
		url = url.trim();

		// Check if the URL already contains a protocol
		if (/^(http:\/\/|https:\/\/)/.test(url)) {
			return url;
		}

		// Remove leading slashes
		url = url.replace(/^\/+/, "");

		// Add 'https://' as default protocol
		return "https://" + url;
	};

	pmw.registerShowVariationEventListener = () => {
		let previousVariationId = null;

		jQuery(".single_variation_wrap").on("show_variation", (event, variation) => {

			// Abort if lazy loading is enabled
			// if (pmwDataLayer?.general?.lazy_load_pmw) return;

			try {
				// If variations_output is disabled, don't fire view_item events from show_variation
				// because the parent product view_item is already triggered on page load
				if (!pmwDataLayer?.shop?.variations_output) return;

				let productId = pmw.getIdBasedOndVariationsOutputSetting(variation.variation_id);

				if (!productId) throw Error("Wasn't able to retrieve a productId");

				// If the variation id is not the same as the previous one, run the function
				if (previousVariationId !== variation.variation_id) {
					pmw.triggerViewItemEventPrep(productId);
					previousVariationId = variation.variation_id;
				}

			} catch (e) {
				console.error(e);
			}
		});
	};

	/**
	 * Initializes the command queue for the application to ensure that commands
	 * are queued and executed in a controlled manner. This function sets up
	 * the underlying mechanism to manage and process commands effectively.
	 *
	 * Typically, the command queue is used by external developers
	 * who want to make sure that their Pixel Manager API calls safely get executed,
	 * no matter if the initialization order of the Pixel Manager is shuffled,
	 * delayed, or lazy loaded.
	 *
	 * @since 1.49.0
	 */
	pmw.initializeCommandQueue = () => {

		const queue = window._pmwq = window._pmwq || [];

		queue.forEach(function (fn) {
			if (typeof fn === "function") {
				try {
					fn();
				} catch (e) {
					pmw.console.error("Error executing queued command:", e);
				}
			}
		});

		// Override push to execute commands immediately now that PMW is ready
		queue.push = function (fn) {
			if (typeof fn === "function") {
				try {
					fn();
				} catch (e) {
					pmw.console.error("Error executing command from queue:", e);
				}
			}
		};
	};

}(window.pmw = window.pmw || {}, jQuery));
